export const TAKER_FEE_RATE = 0.0005; // Binance USDS-M 선물 기본 taker fee (0.05%)

interface TPAndSLInput {
  price: number;
  amount: number;
  tp?: number;
  sl?: number;
  positionType: 'long' | 'short';
  type?: string;
}

interface TPAndSLOutput {
  TP_roe: number; // TP netROE (%)
  SL_roe: number; // SL netROE (%)
}

/**
 * TP/SL 절대 가격 → netROE(%) 변환.
 *
 * netROE = (grossPnL - fee) / amount * 100
 * fee = amount * TAKER_FEE_RATE * 2  (진입 + 청산)
 * amount = 명목가치(notional). leverage는 현재 1로 고정.
 */
export function calculateTPAndSL({ price, amount, tp, sl, positionType }: TPAndSLInput): TPAndSLOutput {
  if (amount <= 0) {
    throw new Error('Amount must be greater than 0');
  }

  const positionSize = amount / price;
  const fee = amount * TAKER_FEE_RATE * 2;

  let grossTP: number;
  let grossSL: number;

  switch (positionType) {
    case 'long':
      grossTP = (tp! - price) * positionSize;
      grossSL = (sl! - price) * positionSize;
      break;
    case 'short':
      grossTP = (price - tp!) * positionSize;
      grossSL = (price - sl!) * positionSize;
      break;
    default:
      throw new Error("Invalid positionType. Must be 'long' or 'short'.");
  }

  return {
    TP_roe: ((grossTP - fee) / amount) * 100,
    SL_roe: ((grossSL - fee) / amount) * 100,
  };
}

export const TPSLRatio = (SL_price: number, TP_price: number): number => {
  if (SL_price === 0) {
    throw new Error('Stop Loss cannot be zero.');
  }
  return TP_price / Math.abs(SL_price);
};

interface CalculateTPInput {
  TP_SL_ratio: number;
  price: number;
  amount: number;
  sl: number;
  positionType: 'long' | 'short';
}

/**
 * netROE ratio 기준으로 TP 가격을 역산한다.
 *
 * SL_roe를 먼저 계산한 뒤, TP_roe = ratio * |SL_roe|로 TP를 결정한다.
 * 수수료가 양쪽에 동일하게 차감되므로, netROE 1:1이 되려면 TP 가격 거리가 SL보다 살짝 더 멀어야 한다.
 */
export function calculateTPFromRatio({ TP_SL_ratio, price, amount, sl, positionType }: CalculateTPInput): number {
  if (amount <= 0) {
    throw new Error('Amount must be greater than 0');
  }

  if (isNaN(TP_SL_ratio) || TP_SL_ratio <= 0) {
    throw new Error('TP_SL_ratio must be a valid number greater than 0');
  }

  const positionSize = amount / price;
  const fee = amount * TAKER_FEE_RATE * 2;

  // SL netROE 계산
  let grossSL: number;
  switch (positionType) {
    case 'long':
      grossSL = (sl - price) * positionSize;
      break;
    case 'short':
      grossSL = (price - sl) * positionSize;
      break;
    default:
      throw new Error("Invalid positionType. Must be 'long' or 'short'.");
  }
  const slRoe = ((grossSL - fee) / amount) * 100;

  // TP_roe = ratio * |SL_roe| → TP 가격 역산
  const tpRoe = TP_SL_ratio * Math.abs(slRoe);
  const grossTP = (tpRoe / 100) * amount + fee;

  let tp: number;
  switch (positionType) {
    case 'long':
      tp = price + grossTP / positionSize;
      break;
    case 'short':
      tp = price - grossTP / positionSize;
      break;
    default:
      throw new Error("Invalid positionType. Must be 'long' or 'short'.");
  }
  return tp;
}

interface PriceToNetRoeInput {
  price: number; // TP 또는 SL 절대 가격
  entry: number; // 진입가
  amount: number; // 명목가치(notional)
  positionType: 'long' | 'short';
}

/**
 * TP/SL 절대 가격 → netROE(%) 변환 (단일 가격 기준).
 *
 * grossPnL = (price - entry) * positionSize  (Long)
 * netROE = (grossPnL - fee) / amount * 100
 */
export function priceToNetRoe({ price, entry, amount, positionType }: PriceToNetRoeInput): number {
  const positionSize = amount / entry;
  const fee = amount * TAKER_FEE_RATE * 2;

  let grossPnL: number;
  switch (positionType) {
    case 'long':
      grossPnL = (price - entry) * positionSize;
      break;
    case 'short':
      grossPnL = (entry - price) * positionSize;
      break;
    default:
      throw new Error("Invalid positionType. Must be 'long' or 'short'.");
  }

  return ((grossPnL - fee) / amount) * 100;
}

interface NetRoeToPriceInput {
  netRoe: number; // netROE (%)
  entry: number; // 진입가
  amount: number; // 명목가치(notional)
  positionType: 'long' | 'short';
}

/**
 * netROE(%) → TP/SL 절대 가격 역변환.
 *
 * grossPnL = (netRoe / 100) * amount + fee
 * Long:  price = entry + grossPnL / positionSize
 * Short: price = entry - grossPnL / positionSize
 */
export function netRoeToPrice({ netRoe, entry, amount, positionType }: NetRoeToPriceInput): number {
  const positionSize = amount / entry;
  const fee = amount * TAKER_FEE_RATE * 2;
  const grossPnL = (netRoe / 100) * amount + fee;

  switch (positionType) {
    case 'long':
      return entry + grossPnL / positionSize;
    case 'short':
      return entry - grossPnL / positionSize;
    default:
      throw new Error("Invalid positionType. Must be 'long' or 'short'.");
  }
}
